import {connect} from 'react-redux';

import {
  AppParametricSelector,
  createSelector,
  makeMapStateToProps,
} from '../../../../../../../srcCommon/helpers/reselectHelpers';
import {
  buildReplyRecipients,
  flattenRecipientMap,
  ReplyTypesEnum,
  VisualRecipientMap,
} from '../../../../../../helpers/models/recipientHelpers';
import {ChannelModel} from '../../../../../../models/api/channelModel';
import {MessageModel} from '../../../../../../models/api/messageModel';
import {VisualRecipientModel} from '../../../../../../models/api/visualRecipientModel';
import {AppState} from '../../../../../../store/appState';
import {
  AutoBccHandlesSelector,
  getUserParticipatingChannels,
  makeGetAutoBccHandlesForChannelsFromRecipients,
} from '../../../../../../store/data/channels/channelsSelectors';
import {MessageViewerEmailActions} from './messageViewerEmailActions';

/*
 * Props.
 */

interface MessageViewerEmailActionsContainerProps {
  message: MessageModel;
  replyChannel: ChannelModel;
  visualRecipients: VisualRecipientMap;
}

/*
 * Selectors.
 */

function getMessage(_state: AppState, props: MessageViewerEmailActionsContainerProps) {
  return props.message;
}

function getReplyChannel(_state: AppState, props: MessageViewerEmailActionsContainerProps) {
  return props.replyChannel;
}

function getVisualRecipients(_state: AppState, props: MessageViewerEmailActionsContainerProps) {
  return props.visualRecipients;
}

function makeGetFlatVisualRecipients() {
  return createSelector([getVisualRecipients], (visualRecipients) => flattenRecipientMap(visualRecipients));
}

function makeGetCanReplyAll<P>(
  getAutoBccHandlesForChannelsFromRecipients: AutoBccHandlesSelector<P>,
  getFlatVisualRecipients: AppParametricSelector<P, ReadonlyArray<VisualRecipientModel>>,
) {
  return createSelector(
    [
      getUserParticipatingChannels,
      getMessage,
      getReplyChannel,
      getFlatVisualRecipients,
      getAutoBccHandlesForChannelsFromRecipients,
    ],
    (
      participatingChannels,
      message,
      replyChannel,
      visualRecipients,
      autoBccHandlesForChannelsFromRecipients,
    ) => {
      if (!replyChannel) {
        return false;
      }

      const replyRecipients = buildReplyRecipients(
        participatingChannels,
        autoBccHandlesForChannelsFromRecipients,
        replyChannel,
        ReplyTypesEnum.ALL,
        visualRecipients,
        message.isInbound,
      );

      return replyRecipients.length > 1;
    },
  );
}

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<MessageViewerEmailActionsContainerProps>()(() => {
  const getFlatVisualRecipients = makeGetFlatVisualRecipients();

  return {
    canReplyAll: makeGetCanReplyAll(
      makeGetAutoBccHandlesForChannelsFromRecipients(getFlatVisualRecipients),
      getFlatVisualRecipients,
    ),
  };
});

export const MessageViewerEmailActionsContainer = connect(mapStateToProps)(MessageViewerEmailActions);
